import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../../utils/api";
import {
  Search,
  Plus,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Users,
} from "lucide-react";
import {
  AdminLayout,
  PageHeader,
  Card,
  Button,
  Badge,
  Grid,
  PaginationControls,
  LoadingSpinner,
  EmptyState,
  useToast,
} from "../components/AdminSharedComponents";

const Client = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const toast = useToast();

  useEffect(() => {
    const fetchClients = async () => {
      setLoading(true);
      try {
        // const response = await api.get("/api/clients");
        const response = await api.get("/api/users?role=client");
        setClients(response.data.data || []);
      } catch (error) {
        console.error("Error fetching clients:", error);
        setClients([]);
        toast.error("Gagal memuat data klien");
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Apakah Anda yakin ingin menghapus klien ini?")) {
      try {
        // await api.delete(`/api/clients/${id}`);
        await api.delete(`/api/users/${id}`);
        const updatedClients = clients.filter((client) => client.id !== id);
        setClients(updatedClients);
        toast.success("Klien berhasil dihapus");
      } catch (error) {
        console.error("Error deleting client:", error);
        toast.error("Gagal menghapus klien");
      }
    }
  };

  // Filter clients based on search term
  const filteredClients = clients.filter(
    (client) =>
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredClients.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);

  // Pagination controls
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  return (
    <AdminLayout>
      <PageHeader
        title="Client Management"
        subtitle="Manage and explore client data"
        actions={
          <Link to="/admin/client/add">
            <Button variant="primary" className="flex items-center gap-1">
              <Plus size={16} />
              <span>Add New Client</span>
            </Button>
          </Link>
        }
      />

      <div className="flex gap-4 items-stretch mb-6">
        {/* Client Stats */}
        <Card
          title="Active Clients"
          accentColor="primary"
          className="w-1/4 flex flex-col justify-between"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Total</span>
            <Badge variant="success">{clients.length}</Badge>
          </div>
          <div className="flex items-center mt-3">
            <Users size={18} className="mr-2 text-primary" />
            <span className="text-xl font-bold">{clients.length}</span>
          </div>
        </Card>

        {/* Search */}
        <Card
          title="Search Clients"
          className="w-3/4 flex flex-col justify-center"
          accentColor="dark"
        >
          <div className="relative">
            <input
              type="text"
              placeholder="Search clients..."
              className="w-full p-2 pl-10 border rounded text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search
              size={16}
              className="absolute left-3 top-2.5 text-primary"
            />
          </div>
        </Card>
      </div>

      {/* Client Table/Cards */}
      <Card title="Client List" accentColor="primary">
        {loading ? (
          <LoadingSpinner message="Memuat data klien..." />
        ) : clients.length === 0 ? (
          <EmptyState
            message="Belum ada klien tersedia"
            action={{
              label: "Tambah Klien Pertama",
              onClick: () => navigate("/admin/client/add"),
            }}
          />
        ) : currentItems.length === 0 ? (
          <EmptyState
            message="Tidak ada klien yang sesuai dengan pencarian"
            action={{
              label: "Reset Pencarian",
              onClick: () => setSearchTerm(""),
            }}
          />
        ) : (
          <>
            {/* Desktop View - Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="p-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentItems.length > 0 ? (
                    currentItems.map((client) => (
                      <tr key={client.id} className="hover:bg-gray-50">
                        <td className="p-3 font-medium text-sm text-primary-dark">
                          {client.name}
                        </td>
                        <td className="p-3 text-gray-700 text-sm">
                          {client.email}
                        </td>
                        <td className="p-3 text-gray-700 text-sm">
                          {new Date(client.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex justify-center space-x-2">
                            <Link to={`/admin/client/edit/${client.id}`}>
                              <Button variant="text" className="p-1">
                                <Edit size={18} />
                              </Button>
                            </Link>
                            <Button
                              variant="text"
                              className="p-1 text-red-600"
                              onClick={() => handleDelete(client.id)}
                            >
                              <Trash2 size={18} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        className="p-4 text-center text-gray-500 text-sm"
                      >
                        No clients found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {filteredClients.length > 0 && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onNext={nextPage}
                onPrev={prevPage}
                onPageChange={paginate}
              />
            )}
          </>
        )}
      </Card>
    </AdminLayout>
  );
};

export default Client;
